// src/pages/CompanyDashboardPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { 
    FaCar, FaSpinner, FaQrcode, FaTimes, FaDownload, FaWrench, 
    FaCheckCircle, FaSearch, FaExclamationTriangle, 
    FaInfoCircle, FaEdit, FaChartLine 
} from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import { 
    PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, 
    BarChart, Bar, XAxis, YAxis, CartesianGrid 
} from 'recharts';

// QR Styling for the large modal view
const qrCodeOptionsModal = {
    size: 256,
    bgColor: "#FFFFFF",
    fgColor: "#000000",
    level: "Q",
    includeMargin: true,
};

const CompanyDashboardPage = () => {
    const { currentUser, userData } = useAuth();
    const [cars, setCars] = useState([]);
    const [stats, setStats] = useState({ total: 0, rented: 0, available: 0, maintenance: 0 });
    const [loading, setLoading] = useState(true);
    const [modalQr, setModalQr] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    
    // Chart Data States
    const [fleetHealthData, setFleetHealthData] = useState([]);
    const [monthlyRevenue, setMonthlyRevenue] = useState([]);

    const qrCodeRef = useRef(null);

    const fetchCompanyCars = useCallback(async () => {
        if (!currentUser || !userData?.companyProfileId) return;
        setLoading(true);

        try {
            // 1. Fetch Cars
            const q = query(collection(db, 'vehicleProfiles'), where('userId', '==', currentUser.uid));
            const querySnapshot = await getDocs(q);
            const carData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // 2. Fetch Rentals for Stats & Revenue
            const rentalsQ = query(
                collection(db, 'rentals'), 
                where('companyId', '==', userData.companyProfileId),
                orderBy('rentalStartDate', 'desc')
            );
            const rentalsSnapshot = await getDocs(rentalsQ);
            const allRentals = rentalsSnapshot.docs.map(doc => doc.data());

            // 3. Process Car Data & Associate Recent Rentals
            const carDataWithRentals = carData.map(car => {
                const carRentals = allRentals
                    .filter(r => r.vehicleProfileId === car.id)
                    .slice(0, 5); // Get last 5 rentals per car
                return { ...car, recentRentals: carRentals };
            });

            setCars(carDataWithRentals);

            // 4. Calculate KPI Stats
            const total = carData.length;
            const rented = carData.filter(car => car.internalData?.status === 'Kirada').length;
            const maintenance = carData.filter(car => ['Bakımda', 'Hasarlı', 'Serviste'].includes(car.internalData?.status)).length;
            const available = total - rented - maintenance;
            
            setStats({ total, rented, available, maintenance });

            // 5. Prepare Pie Chart Data (Fleet Health)
            setFleetHealthData([
                { name: 'Müsait', value: available, color: '#10B981' },
                { name: 'Kirada', value: rented, color: '#EF4444' },
                { name: 'Bakım/Hasarlı', value: maintenance, color: '#F59E0B' },
            ].filter(d => d.value > 0));

            // 6. Prepare Bar Chart Data (Monthly Revenue)
            const revenueMap = {};
            const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
            
            allRentals.forEach(rental => {
                if (rental.rentalStartDate) {
                    const date = new Date(rental.rentalStartDate.seconds * 1000);
                    const monthKey = months[date.getMonth()]; // e.g., "Ocak"
                    // Only consider current year for simplicity, or last 12 months in a real app
                    // Here we aggregate by month name for simplicity
                    revenueMap[monthKey] = (revenueMap[monthKey] || 0) + (Number(rental.totalPrice) || 0);
                }
            });

            // Convert to array and sort by month index
            const currentMonthIndex = new Date().getMonth();
            // Create data for the last 6 months effectively
            const chartData = [];
            for (let i = 5; i >= 0; i--) {
                const d = new Date();
                d.setMonth(currentMonthIndex - i);
                const mName = months[d.getMonth()];
                chartData.push({
                    month: mName,
                    gelir: revenueMap[mName] || 0
                });
            }
            setMonthlyRevenue(chartData);

            // 7. Check Notifications (Maintenance/Insurance Expiry)
            const upcomingNotifications = [];
            const now = new Date();
            const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

            carData.forEach(car => {
                const checkDate = (dateStr, car, type) => {
                    if (!dateStr) return;
                    const expireDate = new Date(dateStr);
                    if (expireDate < now) {
                        upcomingNotifications.push({ 
                            priority: 'critical',
                            car, 
                            message: `${car.vehiclePlate} - ${type} süresi doldu!` 
                        });
                    } else if (expireDate <= thirtyDaysFromNow) {
                        upcomingNotifications.push({ 
                            priority: 'high',
                            car, 
                            message: `${car.vehiclePlate} - ${type} yaklaşıyor.` 
                        });
                    }
                };

                checkDate(car.internalData?.kaskoExpireDate, car, 'Kasko');
                checkDate(car.internalData?.sigortaExpireDate, car, 'Sigorta');
                checkDate(car.internalData?.yagBakimTarihi, car, 'Yağ Bakımı');
                checkDate(car.internalData?.muayeneGecerlilikTarihi, car, 'Muayene');
            });

            setNotifications(upcomingNotifications);

        } catch (error) {
            console.error("Dashboard Error:", error);
        } finally {
            setLoading(false);
        }
    }, [currentUser, userData]);

    useEffect(() => {
        fetchCompanyCars();
    }, [fetchCompanyCars]);

    const handleDownload = () => {
        if (qrCodeRef.current) {
            const svgXML = new XMLSerializer().serializeToString(qrCodeRef.current);
            const dataUrl = `data:image/svg+xml;base64,${btoa(svgXML)}`;
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = "#FFFFFF"; // Ensure white background
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0);
                const pngFile = canvas.toDataURL('image/png');
                const downloadLink = document.createElement('a');
                downloadLink.download = `${modalQr.plate || modalQr.id}-QRCode.png`;
                downloadLink.href = pngFile;
                downloadLink.click();
            };
            img.src = dataUrl;
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* QR Code Modal */}
            {modalQr && (
                <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-[9999] p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl shadow-2xl text-center relative max-w-sm w-full p-8 animate-fadeIn" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setModalQr(null)} className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors p-2 rounded-full hover:bg-red-50">
                            <FaTimes size={24} />
                        </button>
                        <h3 className="text-3xl font-bold mb-2 text-gray-900">{modalQr.vehiclePlate || 'Plaka Yok'}</h3>
                        <p className="text-gray-500 mb-6 text-sm">{modalQr.model}</p>
                        
                        <div className="p-4 bg-white rounded-xl inline-block shadow-inner border border-gray-100">
                            <QRCodeSVG ref={qrCodeRef} value={`https://kodutara.web.app/q/${modalQr.id}`} {...qrCodeOptionsModal} />
                        </div>
                        
                        <div className="mt-8 flex flex-col gap-3">
                            <button onClick={handleDownload} className="w-full flex items-center justify-center px-6 py-3.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 font-semibold transition-all transform hover:scale-[1.02] shadow-lg hover:shadow-xl">
                                <FaDownload className="mr-2" /> PNG İndir
                            </button>
                            <Link to={`/q/${modalQr.id}`} target="_blank" className="text-sm text-gray-500 hover:text-gray-900 underline">
                                Tarayıcıda Aç
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex flex-col justify-center items-center min-h-[80vh]">
                    <FaSpinner className="animate-spin text-5xl text-blue-600 mb-4" />
                    <p className="text-xl text-gray-600 font-medium">Panel Hazırlanıyor...</p>
                </div>
            ) : (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10 max-w-7xl">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
                        <div>
                            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Filo Yönetim Paneli</h1>
                            <p className="text-gray-500 mt-1">Araç durumları ve finansal özet</p>
                        </div>
                        <div className="flex gap-3">
                             <Link to="/company/profile" className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition shadow-sm">
                                Şirket Ayarları
                             </Link>
                        </div>
                    </div>

                    {/* Alerts Section */}
                    {notifications.length > 0 && (
                        <div className="mb-8 grid gap-4">
                            {notifications.map((note, idx) => (
                                <div key={idx} className={`flex items-center p-4 rounded-lg border-l-4 shadow-sm ${
                                    note.priority === 'critical' ? 'bg-red-50 border-red-500 text-red-900' : 'bg-orange-50 border-orange-500 text-orange-900'
                                }`}>
                                    <div className="mr-4 text-xl">
                                        {note.priority === 'critical' ? <FaExclamationTriangle /> : <FaInfoCircle />}
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-semibold">{note.message}</p>
                                    </div>
                                    <Link to={`/company/edit-car/${note.car.id}`} className="px-4 py-1.5 bg-white/50 hover:bg-white rounded-md text-sm font-medium transition">
                                        İncele
                                    </Link>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* KPI Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                        {/* Stat Card 1 */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow relative overflow-hidden group">
                            <div className="absolute right-0 top-0 h-full w-1 bg-blue-500 group-hover:w-2 transition-all"></div>
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-gray-500 text-sm font-semibold uppercase tracking-wider">Toplam Araç</p>
                                    <h3 className="text-4xl font-bold text-gray-900 mt-2">{stats.total}</h3>
                                </div>
                                <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                                    <FaCar size={24} />
                                </div>
                            </div>
                        </div>

                         {/* Stat Card 2 */}
                         <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow relative overflow-hidden group">
                            <div className="absolute right-0 top-0 h-full w-1 bg-red-500 group-hover:w-2 transition-all"></div>
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-gray-500 text-sm font-semibold uppercase tracking-wider">Kirada</p>
                                    <h3 className="text-4xl font-bold text-gray-900 mt-2">{stats.rented}</h3>
                                    <p className="text-xs text-red-500 mt-1 font-medium">
                                        {stats.total > 0 ? `%${((stats.rented / stats.total) * 100).toFixed(0)} kullanım` : '%0'}
                                    </p>
                                </div>
                                <div className="p-3 bg-red-50 text-red-600 rounded-xl">
                                    <FaChartLine size={24} />
                                </div>
                            </div>
                        </div>

                         {/* Stat Card 3 */}
                         <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow relative overflow-hidden group">
                            <div className="absolute right-0 top-0 h-full w-1 bg-green-500 group-hover:w-2 transition-all"></div>
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-gray-500 text-sm font-semibold uppercase tracking-wider">Müsait</p>
                                    <h3 className="text-4xl font-bold text-gray-900 mt-2">{stats.available}</h3>
                                </div>
                                <div className="p-3 bg-green-50 text-green-600 rounded-xl">
                                    <FaCheckCircle size={24} />
                                </div>
                            </div>
                        </div>

                         {/* Stat Card 4 */}
                         <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow relative overflow-hidden group">
                            <div className="absolute right-0 top-0 h-full w-1 bg-yellow-400 group-hover:w-2 transition-all"></div>
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-gray-500 text-sm font-semibold uppercase tracking-wider">Bakım/Servis</p>
                                    <h3 className="text-4xl font-bold text-gray-900 mt-2">{stats.maintenance}</h3>
                                </div>
                                <div className="p-3 bg-yellow-50 text-yellow-600 rounded-xl">
                                    <FaWrench size={24} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Charts Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
                        {/* Revenue Chart */}
                        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-bold text-gray-800">Son 6 Ay Gelir Analizi</h3>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <span className="w-3 h-3 rounded-full bg-blue-500"></span> Gelir (TL)
                                </div>
                            </div>
                            <div className="h-[300px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={monthlyRevenue} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12}} dy={10} />
                                        <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12}} />
                                        <Tooltip 
                                            cursor={{fill: '#f8fafc'}}
                                            contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}}
                                        />
                                        <Bar dataKey="gelir" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={40} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Fleet Status Pie */}
                        <div className="lg:col-span-1 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                            <h3 className="text-lg font-bold text-gray-800 mb-6 text-center">Filo Durumu</h3>
                            <div className="h-[250px] w-full relative">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie 
                                            data={fleetHealthData} 
                                            cx="50%" 
                                            cy="50%" 
                                            innerRadius={60} 
                                            outerRadius={80} 
                                            paddingAngle={5} 
                                            dataKey="value"
                                        >
                                            {fleetHealthData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend verticalAlign="bottom" height={36} iconType="circle" />
                                    </PieChart>
                                </ResponsiveContainer>
                                {/* Center Text */}
                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -mt-4 text-center">
                                    <p className="text-gray-400 text-xs font-semibold uppercase">Toplam</p>
                                    <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Inventory List Header & Search */}
                    <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                        <h2 className="text-2xl font-bold text-gray-800">Araç Listesi</h2>
                        <div className="relative w-full sm:w-auto min-w-[300px]">
                            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input 
                                type="text" 
                                placeholder="Plaka, marka veya model ara..." 
                                className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition shadow-sm"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Inventory Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {cars.filter(car => 
                            (car.vehiclePlate || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                            (car.make || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                            (car.model || '').toLowerCase().includes(searchTerm.toLowerCase())
                        ).map(car => (
                            <div key={car.id} className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 flex flex-col overflow-hidden">
                                {/* Status Strip */}
                                <div className={`h-1.5 w-full ${
                                    car.internalData?.status === 'Kirada' ? 'bg-red-500' : 
                                    (car.internalData?.status === 'Müsait' ? 'bg-green-500' : 'bg-yellow-500')
                                }`}></div>
                                
                                <div className="p-5 flex-1 flex flex-col">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h3 className="text-xl font-bold text-gray-900">{car.vehiclePlate}</h3>
                                            <p className="text-gray-500 text-sm">{car.make} {car.model}</p>
                                        </div>
                                        <button onClick={() => setModalQr(car)} className="text-gray-400 hover:text-gray-900 transition-colors">
                                            <FaQrcode size={20} />
                                        </button>
                                    </div>

                                    <div className="mt-auto space-y-3">
                                        {/* Status Badge */}
                                        <div className="flex items-center justify-between">
                                            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                                                car.internalData?.status === 'Kirada' ? 'bg-red-50 text-red-600' : 
                                                (car.internalData?.status === 'Müsait' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600')
                                            }`}>
                                                {car.internalData?.status || 'Durum Yok'}
                                            </span>
                                            {car.internalData?.fuelType && (
                                                <span className="text-xs text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded">
                                                    {car.internalData.fuelType}
                                                </span>
                                            )}
                                        </div>

                                        {/* Inspection / Muayene & Sigorta Info */}
                                        <div className="text-xs space-y-1 bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-500 font-medium">Muayene:</span>
                                                {car.internalData?.muayeneGecerlilikTarihi ? (
                                                    <span className="font-semibold text-gray-800">{car.internalData.muayeneGecerlilikTarihi}</span>
                                                ) : (
                                                    <Link to={`/company/edit-car/${car.id}`} className="text-blue-600 hover:underline font-medium">+ Muayene Ekle</Link>
                                                )}
                                            </div>
                                            {car.internalData?.sigortaExpireDate && (
                                                <div className="flex justify-between items-center text-gray-500">
                                                    <span>Sigorta:</span>
                                                    <span className="font-medium text-gray-700">{car.internalData.sigortaExpireDate}</span>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="pt-4 border-t border-gray-50 grid grid-cols-2 gap-3">
                                            <Link to={`/company/edit-car/${car.id}`} className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-100 transition">
                                                <FaEdit /> Düzenle
                                            </Link>
                                            <button onClick={() => setModalQr(car)} className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-semibold hover:bg-blue-100 transition">
                                                <FaQrcode /> QR
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanyDashboardPage;